import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TrendPill } from "./TrendPill";

const barColor = (v) => {
  if (v == null) return "bg-secondary";
  if (v >= 80) return "bg-[hsl(var(--success))]";
  if (v >= 60) return "bg-primary";
  if (v >= 40) return "bg-[hsl(var(--warning))]";
  return "bg-destructive/70";
};

/**
 * HealthBar — labeled progress bar with an optional trend pill or CTA
 * when the metric is missing.
 *
 * props:
 *   label            e.g., "Salary Competitiveness"
 *   value            0..100 | null
 *   state            "ok" | "missing"
 *   unlockAction     { label, path } when state === "missing"
 *   trend            { delta, period_days }
 *   explain          hover/title tooltip
 *   compact          smaller layout for the score-breakdown widget
 *   testId
 */
export const HealthBar = ({
  label,
  value,
  state = "ok",
  unlockAction,
  trend,
  explain,
  compact = false,
  testId,
}) => {
  const isMissing = state === "missing" || value == null;
  return (
    <div
      className={compact ? "space-y-1" : "space-y-1.5"}
      data-testid={testId}
      title={explain || undefined}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="text-xs font-medium text-foreground truncate">{label}</div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {trend && !isMissing && (
            <TrendPill
              delta={trend.delta}
              period={trend.period_days ? `${trend.period_days}d` : undefined}
              size="xs"
              testId={testId ? `${testId}-trend` : undefined}
            />
          )}
          <div className="font-mono font-semibold text-xs text-foreground min-w-[36px] text-right">
            {isMissing ? "—" : `${Math.round(value)}%`}
          </div>
        </div>
      </div>
      <div className={`rounded-full bg-secondary overflow-hidden ${compact ? "h-1" : "h-1.5"}`}>
        {!isMissing && (
          <motion.div
            className={`h-full rounded-full ${barColor(value)}`}
            initial={{ width: 0 }}
            animate={{ width: `${Math.max(2, value)}%` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
        )}
      </div>
      {isMissing && unlockAction && (
        <Link
          to={unlockAction.path}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline mt-1"
          data-testid={testId ? `${testId}-unlock` : undefined}
        >
          {unlockAction.label} <ArrowRight size={10} strokeWidth={2} />
        </Link>
      )}
    </div>
  );
};
